import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  CardGiftcard,
  LocalAtm,
  Dehaze,
  Groups,
  Person,
} from "@mui/icons-material";
import {
  Box,
  useMediaQuery,
  useTheme,
  AppBar,
  BottomNavigation,
  BottomNavigationAction, Grid, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { selectCurrentToken } from "../features/slices/authSlice";
import { selectAlertIsOpen } from "../features/slices/alertSlice";

import { useGetCommonBackgroundQuery } from "../features/api/publicApiSlice";

import DesktopHeader from "./Header/DesktopHeader";
import MobileHeader from "./Header/MobileHeader";
import Footer from "./Footer";
import SideNav from "./common/SideNav";
import SnackbarAlert from "./common/SnackbarAlert";
import * as Constant from "../features/constant";
import Drawer from "./common/Drawer";
import { useGetProfileQuery } from "../features/api/userApiSlice";
import SpinWheelSideNavBar from "../components/common/SpinWheelSideNavBar";
import AppDownloadOverlay from "./common/AppDownloadOverlay";
import { useGetAppDownloadOverlayConfigQuery } from "../features/api/homeApiSlice";
import { SvgIcon } from "@mui/material";
import Image from "./common/Image";
import SeoContent from "./SeoContent";
import { isRunningInApp } from "../utils/isRunningInApp";

const Layout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const alertIsOpen = useSelector(selectAlertIsOpen);
  const [showPopup, setShowPopup] = useState(true);
  const [showHeaderFooter, setShowHeaderFooter] = useState(true);
  const location = useLocation();
  const [selectedGameCategory, setSelectedGameCategory] = useState("");

  const {
    data: commonBackground,
    isLoading: isCommonBackgroundLoading,
    isSuccess: isCommonBackgroundSuccess,
  } = useGetCommonBackgroundQuery({ prefetch: true });

  const {
    data: appDownloadOverlayConfig,
  } = useGetAppDownloadOverlayConfigQuery({ prefetch: true });

  const shouldShowAppDownloadOverlay =
  mobileView &&
  !isRunningInApp() &&
  appDownloadOverlayConfig?.result?.status === "ACTIVE";

  const appDownloadOverlayPosition =
  appDownloadOverlayConfig?.result?.value === "bottom"
    ? "bottom"
    : "top"; // default fallback

  const handleClose = () => {
    setShowPopup(false);
  };

  const triggerLiveChat = () => {
    if (
      typeof window.LiveChatWidget !== "undefined" &&
      window.LiveChatWidget.call
    ) {
      window.LiveChatWidget.call("maximize");
    }
  };

  const {
    data: user,
    isFetching: isUserFetching,
    isLoading: isUserLoading,
  } = useGetProfileQuery({ prefetch: true });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleButtonClick = () => {
    setIsDrawerOpen(true); // Always open the drawer
  };

  const drawerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 100);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    switch (location.pathname) {
      case Constant.getPath("/goldenegg"):
        setShowHeaderFooter(false);
        break;

      default:
        setShowHeaderFooter(true);
    }
  }, [location.pathname]);


  const ThinDehaze = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.0" />
    </SvgIcon>
  );
  return (

    <Box
      display="flex"
      flexDirection="column"
      backgroundColor={theme.palette.background.secondaryPaper}
      minHeight="100vh"
    >
      {alertIsOpen && <SnackbarAlert />}
      {<SideNav />}
      {shouldShowAppDownloadOverlay && <AppDownloadOverlay position={appDownloadOverlayPosition} />}
      {process.env.REACT_APP_SHOW_SPINWHEEL === 'true' && <SpinWheelSideNavBar />}

      {showHeaderFooter && (
        <header style={{
          position: "sticky",
          zIndex: 999
        }}>
          {mobileView ? (
            <>
              <MobileHeader
                background={
                  commonBackground?.mobileBackground[0]?.thumbnailUrl
                }
              />
            </>
          ) : (
            <DesktopHeader
              background={commonBackground?.webBackground[0]?.thumbnailUrl}
            />
          )}
        </header>

      )}

      <Box flex={1}>
        <Outlet
          context={{
            selectedGameCategory,
            setSelectedGameCategory,
          }}
        />
        {!mobileView && process.env.REACT_APP_SEO && (
          <SeoContent />
        )}
      </Box>

      {showHeaderFooter && (
        <>
          {!mobileView && (
            <footer>
              <Footer
                background={commonBackground?.webBackground[1]?.thumbnailUrl}
                contactDetails={commonBackground?.result}
              />
            </footer>
          )}

          {mobileView && (
            <footer style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 999,
            }}>
              <BottomNavigation
                sx={{
                  backgroundColor: theme.palette.bottomNav,
                  paddingBottom: 'calc(env(safe-area-inset-bottom) / 2)',
                }}
                showLabels
                // value={value}
                onChange={(event, newValue) => {
                  if (newValue === 0) {
                    setIsDrawerOpen(true);
                  } else {
                    const adjustedIndex = newValue;

                    switch (adjustedIndex) {
                      case 1:
                        if (process.env.REACT_APP_REPLACE_AFFILIATE_WITH_REFERRAL === 'true') {
                          if (token) {
                            navigate(Constant.getPath("/referral"));
                          } else {
                            navigate(Constant.getPath("/login"));
                          }
                          break;
                        }

                        if (process.env.REACT_APP_FOOTER_REPLACE_AFFILIATE_WITH_APP === 'true') {
                          const apkUrl = `${window.location.origin}/${process.env.REACT_APP_APK}`;
                          const appScheme = process.env.REACT_APP_APK_SCHEME;
                          let hasAppOpened = false;

                          const handleVisibilityChange = () => {
                            if (document.hidden) {
                              hasAppOpened = true;
                            }
                          };

                          const handleOpened = () => { hasAppOpened = true; };
                          window.addEventListener("blur", handleOpened);
                          document.addEventListener("visibilitychange", handleVisibilityChange);

                          const iframe = document.createElement("iframe");
                          iframe.style.display = "none";
                          iframe.src = appScheme;
                          document.body.appendChild(iframe);

                          setTimeout(() => {
                            window.removeEventListener("blur", handleOpened);
                            document.removeEventListener("visibilitychange", handleVisibilityChange);
                            document.body.removeChild(iframe);

                            if (!hasAppOpened) {
                              window.open(apkUrl, "_blank");
                            }
                          }, 1500);
                          break;
                        }
                        const affiliateSite = process.env.REACT_APP_AFFILIATE_SITE;
                        if (affiliateSite) {
                          window.open(affiliateSite, '_blank');
                        } else {
                          if (token) {
                            navigate(Constant.getPath("/affiliate"), {
                              state: {
                                screen: "Affiliate",
                              }
                            });
                          } else {
                            navigate(Constant.getPath("/login"));
                          }
                        }
                        break;
                      case 2:
                        if (token) {
                          navigate(Constant.getPath("/profileWallet"), {
                            state: {
                              screen: "Deposit",
                            },
                          });
                        } else {
                          navigate(Constant.getPath("/login"));
                        }
                        break;
                      case 3:
                        if (token) {
                          navigate(Constant.getPath("/promocenter"), {
                            state: {
                              isNotProfileWallet: true,
                            },
                          });
                        } else {
                          navigate(Constant.getPath("/promotion"));
                        }
                        break;
                      case 4:
                        if (token) {
                          navigate(Constant.getPath("/profileWallet"));
                        } else {
                          navigate(Constant.getPath("/login"));
                        }
                        break;

                      default:

                    }
                  }
                }}
              >
                <BottomNavigationAction
                  sx={{
                    color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || "white", "& .MuiSvgIcon-root": { color: theme.palette.bottomNavIcon, fontSize: "28px", },
                    width: "20%",
                    minWidth: "20%",
                    maxWidth: "20%",
                    "& .MuiSvgIcon-root": {
                      color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT,
                      fontSize: "28px",
                    },
                  }}
                  label={t("footer.menu")}
                  icon={process.env.REACT_APP_BOTTOMNAV_USE_THIN_DEHAZE === 'true' && <ThinDehaze /> || <Dehaze />}
                  onClick={handleButtonClick}
                />
                <BottomNavigationAction
                  sx={{
                    color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || "white",
                    "& .MuiSvgIcon-root": { color: theme.palette.bottomNavIcon },
                    width: "20%",
                    minWidth: "20%",
                    maxWidth: "20%",
                    "& .MuiSvgIcon-root": {
                      color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT,
                      fontSize: "28px",
                    },
                  }}
                  label={
                    process.env.REACT_APP_REPLACE_AFFILIATE_WITH_REFERRAL === 'true'
                      ? t("footer.referral")
                      : process.env.REACT_APP_FOOTER_REPLACE_AFFILIATE_WITH_APP === 'true'
                        ? t("footer.appdownload")
                        : t("footer.affiliate")
                  }
                  icon={
                    process.env.REACT_APP_REPLACE_AFFILIATE_WITH_REFERRAL === 'true' ? (
                      process.env.REACT_APP_BOTTOMNAV_REFERRAL ? (
                        <img
                          src={process.env.REACT_APP_BOTTOMNAV_REFERRAL}
                          alt="Referral Icon"
                          style={{ width: 30, height: 30 }}
                        />
                      ) : (
                        <Groups />
                      )
                    ) : process.env.REACT_APP_FOOTER_REPLACE_AFFILIATE_WITH_APP === 'true' ? (
                      <img
                        src={process.env.REACT_APP_HOME_APP_DOWNLOAD}
                        alt={Constant.ALT_TAG_ICON_APP_DOWNLOAD}
                        style={{ width: 23, height: 23 }}
                      />
                    ) : (
                      process.env.REACT_APP_BOTTOMNAV_REFERRAL ? (
                        <img
                          src={process.env.REACT_APP_BOTTOMNAV_REFERRAL}
                          alt="Referral Icon"
                          style={{ width: 30, height: 30 }}
                        />
                      ) : (
                        <Groups />
                      )
                    )
                  }
                />
                <BottomNavigationAction
                  sx={{
                    color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || "white", "& .MuiSvgIcon-root": { color: theme.palette.bottomNavIcon },
                    width: "20%",
                    minWidth: "20%",
                    maxWidth: "20%",
                    "& .MuiSvgIcon-root": {
                      color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT,
                      fontSize: "28px",
                    },
                  }}
                  label={t("footer.deposit")}
                  icon={
                    process.env.REACT_APP_BOTTOMNAV_DEPOSIT ? (
                      <img
                        src={process.env.REACT_APP_BOTTOMNAV_DEPOSIT}
                        alt="Deposit Icon"
                        style={{ width: 30, height: 30 }}
                      />
                    ) : (
                      <LocalAtm />
                    )
                  }
                />
                <BottomNavigationAction
                  sx={{
                    color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || "white", "& .MuiSvgIcon-root": { color: theme.palette.bottomNavIcon },
                    width: "20%",
                    minWidth: "20%",
                    maxWidth: "20%",
                    "& .MuiSvgIcon-root": {
                      color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT,
                      fontSize: "28px",
                    },
                  }}
                  label={t("footer.promotion")}
                  icon={
                    process.env.REACT_APP_BOTTOMNAV_PROMOTION ? (
                      <img
                        src={process.env.REACT_APP_BOTTOMNAV_PROMOTION}
                        alt="Promotion Icon"
                        style={{ width: 30, height: 30 }}
                      />
                    ) : (
                      <CardGiftcard />
                    )
                  }
                />
                <BottomNavigationAction
                  sx={{
                    color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || "white", "& .MuiSvgIcon-root": { color: theme.palette.bottomNavIcon },
                    width: "20%",
                    minWidth: "20%",
                    maxWidth: "20%",
                    "& .MuiSvgIcon-root": {
                      color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT,
                      fontSize: "28px",
                    },
                  }}
                  label={t("footer.account")}
                  icon={
                    process.env.REACT_APP_BOTTOMNAV_ACCOUNT ? (
                      <img
                        src={process.env.REACT_APP_BOTTOMNAV_ACCOUNT}
                        alt="Account Icon"
                        style={{ width: 30, height: 30 }}
                      />
                    ) : (
                      <Person />
                    )
                  }
                />
              </BottomNavigation>

              {isDrawerOpen && (
                <div ref={drawerRef} className="drawer-container">
                  <Drawer user={user?.result} selectedGameCategory={selectedGameCategory} setSelectedGameCategory={setSelectedGameCategory} />
                </div>
              )}
            </footer>
          )}
        </>
      )}
    </Box>
  );
};

export default Layout;
